"use client"
import type { ReviewPlace } from "./jump-to-string"

/** Pretty label for a bare path: "" → "Home", else the path itself. */
function placeLabel(path: string): string {
  return path === "" ? "Home" : path
}

/**
 * Collapsible list of the pages a shared-UI / off-page string renders on, so a
 * reviewer can open one and visually confirm the (often long) translation in
 * context — a `chromeLike` string isn't tied to a single page, so the panel's
 * auto-jump only ever shows one of them (AHD-7886).
 *
 * Real `<a>` links: a plain click soft-navs in place (`onPreview` keeps the
 * cockpit mounted and re-highlights), while ⌘/Ctrl/middle-click opens the page
 * in a new tab for side-by-side comparison. Renders nothing when there's no
 * located page (truly un-crawled chrome) so it never shows an empty disclosure.
 */
export function PlacesPanel({
  places,
  onPreview,
}: {
  places: ReviewPlace[]
  onPreview: (href: string) => void
}) {
  if (places.length === 0) return null
  return (
    <details className="ahead-review-places">
      <summary>Show where this appears ({places.length})</summary>
      <ul>
        {places.map((p) => (
          <li key={p.href}>
            <a
              href={p.href}
              onClick={(e) => {
                // Defer to the browser for new-tab intents (modifier / non-primary
                // click); otherwise soft-nav in place and re-highlight.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                e.preventDefault()
                onPreview(p.href)
              }}
            >
              {placeLabel(p.path)}
            </a>
          </li>
        ))}
      </ul>
    </details>
  )
}
